import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { getNavigation } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";
import { normalizeMenu, isExternal } from "../lib/siteNav";
import { AnnouncementBar } from "./AnnouncementBar";
import logoFullColor from "@/assets/logo-fullcolor.svg";

// Global navigation (EP-001 v1.1: D19, D21, D28).
// - Solid bar with a thin bottom border; no glass, blur or motion.
// - Desktop (1024px and up): top-level links plus disclosure menus for items with sub-links.
//   Menus open on click or Enter/Space, close with Escape (focus returns to the button),
//   on outside click, when focus leaves, and on navigation.
// - Below 1024px: a right-side drawer (a modal dialog) with sub-links grouped under their
//   parent. Focus moves into it, stays inside it, and returns to the menu button on close.

const testId = (label) => `nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const linkProps = (item) =>
    item.openInNewTab || isExternal(item.url)
        ? { href: item.url, ...(item.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
        : null;

// Renders an internal route with react-router and anything else as a plain link.
const NavLink = ({ item, className, onClick, current, ...rest }) => {
    const external = linkProps(item);
    const aria = current ? { "aria-current": "page" } : {};
    return external ? (
        <a {...external} className={className} onClick={onClick} {...aria} {...rest}>
            {item.label}
        </a>
    ) : (
        <Link to={item.url} className={className} onClick={onClick} {...aria} {...rest}>
            {item.label}
        </Link>
    );
};

const pathOf = (url) => (url || "").split("#")[0].replace(/\/$/, "") || "/";

const DesktopMenu = ({ item, open, onOpen, onClose, currentPath }) => {
    const id = useId();
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);
    const active = item.children.some((c) => pathOf(c.url) === currentPath);

    const onKeyDown = (e) => {
        if (e.key === "Escape" && open) {
            e.stopPropagation();
            onClose();
            buttonRef.current?.focus();
        }
        if (e.key === "ArrowDown" && document.activeElement === buttonRef.current) {
            e.preventDefault();
            onOpen();
            requestAnimationFrame(() => wrapRef.current?.querySelector("ul a")?.focus());
        }
    };

    const onBlur = (e) => {
        if (open && !wrapRef.current?.contains(e.relatedTarget)) onClose();
    };

    return (
        <div ref={wrapRef} className="relative" onKeyDown={onKeyDown} onBlur={onBlur}>
            <button
                ref={buttonRef}
                type="button"
                aria-expanded={open}
                aria-controls={id}
                onClick={() => (open ? onClose() : onOpen())}
                data-testid={testId(item.label)}
                className={`inline-flex h-11 items-center gap-1 rounded-control px-3 text-sm font-medium transition-colors duration-150 hover:text-brand-text ${
                    active || open ? "text-brand-text" : "text-brand-muted"
                }`}
            >
                {item.label}
                <ChevronDown className={`h-4 w-4 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {open && (
                <ul
                    id={id}
                    className="absolute left-0 top-full z-10 mt-2 min-w-[232px] rounded-card border border-brand-line bg-brand-bg p-2"
                >
                    {item.children.map((child) => {
                        const current = pathOf(child.url) === currentPath;
                        return (
                            <li key={child._key || child.url}>
                                <NavLink
                                    item={child}
                                    current={current}
                                    onClick={onClose}
                                    className={`block rounded-control px-3 py-2.5 text-sm transition-colors duration-150 hover:bg-brand-surface ${
                                        current ? "font-semibold text-brand-text" : "text-brand-text"
                                    }`}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const MobileDrawer = ({ items, cta, onClose, currentPath, returnFocusRef }) => {
    const panelRef = useRef(null);
    const closeRef = useRef(null);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        const returnTo = returnFocusRef.current;
        return () => {
            document.body.style.overflow = previousOverflow;
            returnTo?.focus();
        };
    }, [returnFocusRef]);

    const onKeyDown = (e) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
            return;
        }
        if (e.key !== "Tab") return;
        const focusable = panelRef.current?.querySelectorAll("a[href], button:not([disabled])");
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const rowClass = (current) =>
        `block rounded-control px-3 py-3 text-base transition-colors duration-150 hover:bg-brand-surface ${
            current ? "font-semibold text-brand-text" : "text-brand-text"
        }`;

    return (
        <div className="fixed inset-0 z-[60] lg:hidden" onKeyDown={onKeyDown}>
            <div className="absolute inset-0 bg-[#081a3d]/50" onClick={onClose} aria-hidden="true" />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Site menu"
                data-testid="mobile-menu"
                className="absolute inset-y-0 right-0 flex w-[min(360px,88vw)] flex-col overflow-y-auto border-l border-brand-line bg-brand-bg"
            >
                <div className="flex h-16 items-center justify-between px-5">
                    <span className="text-sm font-semibold text-brand-text">Menu</span>
                    <button
                        ref={closeRef}
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="grid h-11 w-11 place-items-center rounded-control border border-brand-control-border text-brand-text"
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
                <nav aria-label="Primary" className="flex-1 px-3 pb-4">
                    <ul className="space-y-1">
                        {items.map((item) =>
                            item.children?.length ? (
                                <li key={item._key || item.url} className="pt-3">
                                    <span className="block px-3 pb-1 text-xs font-semibold text-brand-muted">
                                        {item.label}
                                    </span>
                                    <ul>
                                        {item.children.map((child) => (
                                            <li key={child._key || child.url}>
                                                <NavLink
                                                    item={child}
                                                    current={pathOf(child.url) === currentPath}
                                                    onClick={onClose}
                                                    data-testid={`${testId(child.label)}-mobile`}
                                                    className={rowClass(pathOf(child.url) === currentPath)}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ) : (
                                <li key={item._key || item.url}>
                                    <NavLink
                                        item={item}
                                        current={pathOf(item.url) === currentPath}
                                        onClick={onClose}
                                        data-testid={`${testId(item.label)}-mobile`}
                                        className={rowClass(pathOf(item.url) === currentPath)}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </nav>
                <div className="border-t border-brand-line p-5">
                    <a
                        href={cta.url}
                        {...cta.externalProps}
                        onClick={onClose}
                        className="flex h-11 w-full items-center justify-center gap-1.5 rounded-control bg-brand-primary px-4 text-sm font-semibold text-brand-on-primary transition-colors duration-150 hover:bg-brand-primary-hover"
                    >
                        {cta.label}
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export const Navbar = () => {
    const { theme, toggle } = useTheme();
    const { pathname } = useLocation();
    const currentPath = pathOf(pathname);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const menuButtonRef = useRef(null);
    const headerRef = useRef(null);
    const [nav, setNav] = useState(() => peek(getNavigation) ?? null);

    useEffect(() => {
        let active = true;
        getNavigation()
            .then((data) => {
                if (active && data) setNav(data);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    // Close menus on navigation.
    useEffect(() => {
        setOpenMenu(null);
        setDrawerOpen(false);
    }, [pathname]);

    // Close an open desktop menu on a click outside the header.
    useEffect(() => {
        if (!openMenu) return undefined;
        const onPointer = (e) => {
            if (!headerRef.current?.contains(e.target)) setOpenMenu(null);
        };
        document.addEventListener("pointerdown", onPointer);
        return () => document.removeEventListener("pointerdown", onPointer);
    }, [openMenu]);

    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    const items = normalizeMenu(nav?.menuItems);
    const ctaUrl = nav?.primaryCta?.url || "/contact";
    const cta = {
        label: nav?.primaryCta?.label || "Schedule a Conversation",
        url: ctaUrl,
        externalProps: isExternal(ctaUrl) ? { target: "_blank", rel: "noopener noreferrer" } : {},
    };

    return (
        <header ref={headerRef} data-testid="main-navbar" className="fixed inset-x-0 top-0 z-50">
            <AnnouncementBar
                enabled={nav?.announcementEnabled}
                text={nav?.announcementText}
                url={nav?.announcementUrl}
            />
            <div className="border-b border-brand-line bg-brand-bg">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:h-[72px]">
                    <Link to="/" data-testid="logo-link" className="flex shrink-0 items-center" aria-label="EasiLinked home">
                        {/* Full-color logo in both themes; in dark mode it sits on a white plate so the Dark Blue lettering stays legible (D32). */}
                        <span className="-ml-2 inline-flex rounded-control bg-white px-2 py-1.5">
                            <img
                                src={logoFullColor}
                                alt="EasiLinked — Managed Network & IT Services"
                                width="176"
                                height="40"
                                className="block h-9 w-auto sm:h-10"
                            />
                        </span>
                    </Link>

                    <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                        {items.map((item) =>
                            item.children?.length ? (
                                <DesktopMenu
                                    key={item._key || item.url}
                                    item={item}
                                    open={openMenu === (item._key || item.url)}
                                    onOpen={() => setOpenMenu(item._key || item.url)}
                                    onClose={() => setOpenMenu(null)}
                                    currentPath={currentPath}
                                />
                            ) : (
                                <NavLink
                                    key={item._key || item.url}
                                    item={item}
                                    current={pathOf(item.url) === currentPath}
                                    data-testid={testId(item.label)}
                                    className={`inline-flex h-11 items-center rounded-control px-3 text-sm font-medium transition-colors duration-150 hover:text-brand-text ${
                                        pathOf(item.url) === currentPath ? "text-brand-text" : "text-brand-muted"
                                    }`}
                                />
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={toggle}
                            data-testid="theme-toggle"
                            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                            className="grid h-11 w-11 place-items-center rounded-control border border-brand-line text-brand-text transition-colors duration-150 hover:bg-brand-surface"
                        >
                            {theme === "dark" ? (
                                <Sun className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <Moon className="h-4 w-4" aria-hidden="true" />
                            )}
                        </button>

                        <a
                            href={cta.url}
                            {...cta.externalProps}
                            data-testid="nav-book-cta"
                            className="hidden h-11 items-center gap-1.5 rounded-control bg-brand-primary px-5 text-sm font-semibold text-brand-on-primary transition-colors duration-150 hover:bg-brand-primary-hover sm:inline-flex"
                        >
                            {cta.label}
                            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                        </a>

                        <button
                            ref={menuButtonRef}
                            type="button"
                            onClick={() => setDrawerOpen(true)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Open menu"
                            aria-haspopup="dialog"
                            aria-expanded={drawerOpen}
                            className="grid h-11 w-11 place-items-center rounded-control border border-brand-line text-brand-text lg:hidden"
                        >
                            <Menu className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>

            {drawerOpen && (
                <MobileDrawer
                    items={items}
                    cta={cta}
                    onClose={closeDrawer}
                    currentPath={currentPath}
                    returnFocusRef={menuButtonRef}
                />
            )}
        </header>
    );
};
